import clsx from 'clsx';
import React from 'react';

import { assert } from '@gitbook/util-ts';

import type { ButtonKind, ButtonProps } from '../Button';

export interface ButtonGroupProps {
    children: React.ReactNode;

    /**
     * **How buttons in the group are shown in relation to each other.**
     *
     * - **Separated**: Shows the buttons tightly grouped with no gap. This can be used for a split button.
     * - **Segmented**: Show the buttons with a consistent gap between them. This will remove borders on individual buttons.
     */
    kind?: 'separated' | 'segmented';

    /** In which direction buttons are stacked. */
    orientation?: React.AriaAttributes['aria-orientation'];

    /** Show separators of a kind between buttons. Set it to `undefined` to handle separators yourself. Only applicable when `kind` is set to `segmented`. */
    separatorKind?: ButtonKind;

    /** Show border on the container. Only applicable when `kind` is set to `segmented`. */
    border?: boolean;

    /**
     * Render the group with fully-rounded (pill) outer corners instead of the default
     * `rounded-md`. Only applicable when `kind` is set to `segmented`.
     */
    rounded?: boolean;

    /** Whether to wrap buttons when the container gets too big. */
    wrap?: boolean;

    /** If true, disables all the buttons within the group. */
    disabled?: boolean;

    /**
     * **Can be used to optionally style button container.**
     *
     * - Supply one or more classnames (`text-ui-standout`, `text-danger`, ...)
     * - You can also use generic [Tailwind classes](https://tailwindcss.com/docs/utility-first) (`ml-4`, `blur`, ...) if they’re made available in our [stylesheet](https://github.com/GitbookIO/gitbook-x/blob/main/packages/spine-css/tailwind.css).
     * - Separate multiple classnames by a space.
     */
    className?: string;
}

/**
 * Multiple [Buttons](https://storybook.gitbook.dev/?path=/docs/components-button-button--docs) can be grouped together by wrapping them with a ButtonGroup component.
 *
 * - The buttons should be immediate children of the ButtonGroup component.
 * - The buttons can be stacked in a row (`orientation=horizontal`) or column (`orientation=vertical`).
 */
export function ButtonGroup({
    ref,
    ...props
}: ButtonGroupProps & React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) {
    const {
        children,
        className,
        disabled = false,
        kind = 'separated',
        separatorKind,
        orientation = 'horizontal',
        wrap = false,
        border = true,
        rounded = false,
        ...rest
    } = props;

    return (
        <div
            ref={ref}
            role="group"
            data-segmented-button-group={kind === 'segmented' || undefined}
            data-with-border={border ? '' : undefined}
            className={clsx(
                'group relative flex max-w-full min-w-0 aria-[orientation=vertical]:flex-col',
                wrap ? 'flex-wrap' : 'flex-nowrap',
                kind === 'segmented'
                    ? [
                          'size-fit shrink-0 grow-0 *:rounded-none *:border-0 [&>*:focus]:z-10',
                          rounded ? 'rounded-full' : 'rounded-md',
                          border && 'border',
                          orientation === 'vertical'
                              ? rounded
                                  ? '[&>*:first-child]:rounded-t-full [&>*:last-child]:rounded-b-full'
                                  : '[&>*:first-child]:rounded-t-md [&>*:last-child]:rounded-b-md'
                              : rounded
                                ? '[&>*:first-child]:rounded-l-full [&>*:last-child]:rounded-r-full'
                                : '[&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md',
                      ]
                    : 'gap-2',
                className
            )}
            aria-disabled={disabled}
            aria-orientation={orientation}
            {...rest}
        >
            {React.Children.map(children, (child, index) => {
                if (!child) {
                    return;
                }
                assert(
                    React.isValidElement<{
                        disabled: ButtonProps['disabled'];
                        orientation?: React.AriaAttributes['aria-orientation'];
                    }>(child)
                );
                return (
                    <>
                        {kind === 'segmented' && separatorKind && index !== 0 ? (
                            <ButtonGroupSeparator
                                kind={separatorKind}
                                orientation={orientation}
                                disabled={disabled}
                            />
                        ) : null}
                        {React.cloneElement(child, {
                            disabled: child.props.disabled ?? disabled,
                            ...('label' in child.props ? {} : { orientation }),
                        })}
                    </>
                );
            })}
        </div>
    );
}

export interface ButtonGroupSeparatorProps {
    kind: ButtonKind;
    orientation?: React.AriaAttributes['aria-orientation'];
    disabled?: boolean;
}

export function ButtonGroupSeparator(props: ButtonGroupSeparatorProps) {
    const { kind, orientation = 'horizontal', disabled } = props;
    return (
        <span
            className={`block ${
                {
                    primary: 'bg-neutral-solid-hover',
                    merge: 'bg-merge-solid-hover',
                    danger: 'bg-danger-solid-hover',
                    upgrade: 'bg-upgrade-solid-hover',
                    success: 'bg-success-solid-hover',
                }[kind] ?? 'bg-neutral-ui-hover'
            } ${orientation === 'vertical' ? 'h-[1px] w-full' : 'w-[1px]'} ${disabled ? 'transform-none cursor-not-allowed opacity-disabled' : null}`}
        />
    );
}
